import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Navigation from '../components/layout/Navigation';

const ForZaria: React.FC = () => {
  return (
    <ForZariaContainer>
      <Navigation />
      
      <MessageContent
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <MessageHeader>
          <Title
            as={motion.h1}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            For My Beautiful Zaria 💜
          </Title>
          <Subtitle
            as={motion.p}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            From my heart to yours
          </Subtitle>
        </MessageHeader>

        <MessageBody>
          <LetterContainer
            as={motion.div}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <MessageText>
              <p>My love Zaria,</p>
              
              <p>
                As I sit here reflecting on our incredible journey together, my heart overflows with 
                love and gratitude for everything you are and everything we've built together. 
                Two years ago, you came into my life unexpectedly. I deeply wanted to be more than just your friend. I still can't explain why I was so drawn to you.
                Something about you just clicked with me. Immediately. One instance where my heart and my mind were absolutely in sync.
                I saw a lifetime with you when I looked into your eyes.
              </p>
              
              <p>
                Every day with you feels like a new adventure. Your laugh lights up my world, 
                your smile makes everything better, and your love gives me strength I never knew I had. 
                You've taught me what it truly means to love unconditionally and to be loved in return fully. When my insecurities and fears would try to creep in, you would be there to remind me that I am loved by you.
                I hope that I have been able to do the same for you.
              </p>
              
              <p>
                From our silly inside jokes to our deep midnight conversations, from dancing in the 
                kitchen to planning our dreams together - every moment with you is a treasure I hold close to my heart. 
                I am endlessly grateful for you, Zaria.
              </p>
              
              <p>
                Thank you for being my partner, my best friend, my safe haven, and my greatest love. 
                Thank you for choosing me every day and for letting me choose you. Thank you for 
                building this beautiful life with me, one memory at a time.
              </p>
              
              <p>
                Here's to many more years of love, laughter, adventures, and growing old together. 
                I love you more than words can express, and I'm so grateful to call you mine. 
                I'm so excited to see what the future holds for us. Happy 2 years of love.
              </p>
              
              <p className="signature">
                Forever yours,<br />
                Anisa 💜
              </p>
            </MessageText>
          </LetterContainer>
        </MessageBody>
      </MessageContent>
    </ForZariaContainer>
  );
};

const ForZariaContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  position: relative;
  overflow-x: hidden;
`;

const MessageContent = styled(motion.div)`
  padding: 120px 2rem 4rem;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 100px 1.5rem 3rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 80px 1rem 2rem;
  }
`;

const MessageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  background: ${({ theme }) => theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  text-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.8rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.8;
  font-style: italic;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const MessageBody = styled.div`
  display: flex;
  justify-content: center;
`;

const LetterContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: 3rem;
  box-shadow: ${({ theme }) => theme.shadows.large};
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 2rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const MessageText = styled.div`
  color: ${({ theme }) => theme.colors.light};
  line-height: 1.8;
  font-size: 1.1rem;
  
  p {
    margin-bottom: 1.5rem;
    
    &:first-child {
      font-size: 1.2rem;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.accent1};
    }
    
    &.signature {
      margin-top: 2rem;
      text-align: right;
      font-style: italic;
      color: ${({ theme }) => theme.colors.accent1};
      font-weight: 500;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    
    p:first-child {
      font-size: 1.1rem;
    }
  }
`;

export default ForZaria;